/*
 * What each company's footer says (CompanyFooter.tsx), one entry per
 * SITE_FOOTER_SCOPE that has a footer of its own. Copy and addresses are the
 * owner's; change them here, in one place.
 *
 * The column titles are keys since S48 (2026-10-29): the words are the chosen
 * language's, from the dictionary (CompanyFooter.tsx), and so is LinkedIn's
 * name, built from the logo's.
 *
 * THE LOGOS are the companies' own files, downloaded with the owner's consent
 * and bundled rather than linked: a reader's browser then asks nothing of
 * another company's CDN on every page, and the footer does not break the day a
 * file there moves. Both are white-only, for dark backgrounds, so in light mode
 * the stylesheet darkens them (.site-footer-logo).
 */

import accobatLogo from '../assets/accobat-logo-white.png';
import accotoolLogo from '../assets/accotool-logo-white.png';
import type { CompanyFooterContent } from './CompanyFooter.js';

const ACCOTOOL_SITE = 'https://www.accotool.com/';

/**
 * accotool.com's own footer (the owner, 2026-10-08), so a reader who came from
 * there sees the same way back. The one link left out on purpose is that site's
 * "Knowledge Base", which is this site.
 */
export const ACCOTOOL_FOOTER: CompanyFooterContent = {
  site: ACCOTOOL_SITE,
  logo: { src: accotoolLogo, alt: 'accoTOOL', width: 62, height: 42 },
  strapline: 'Enterprise writeback in Power BI, without the enterprise price.',
  linkedIn: { href: 'https://www.linkedin.com/company/accotool/?viewAsMember=true' },
  columns: [
    {
      title: 'product',
      links: [
        { label: 'accoPLANNING', href: `${ACCOTOOL_SITE}accoplanning.html` },
        { label: 'accoMASTERDATA', href: `${ACCOTOOL_SITE}accomasterdata.html` },
        { label: 'accoCOMMENT', href: `${ACCOTOOL_SITE}accocomment.html` },
        { label: 'Pricing', href: `${ACCOTOOL_SITE}pricing.html` },
      ],
    },
    {
      title: 'resources',
      links: [
        { label: 'Blog', href: `${ACCOTOOL_SITE}blog.html` },
        { label: 'News', href: `${ACCOTOOL_SITE}news.html` },
      ],
    },
    {
      title: 'contact',
      links: [
        { label: 'Sales', href: `${ACCOTOOL_SITE}contact-sales.html` },
        { label: 'Support', href: `${ACCOTOOL_SITE}contact-support.html` },
        { label: 'Book a meeting', href: `${ACCOTOOL_SITE}book-a-meeting.html` },
      ],
    },
  ],
};

/**
 * Accobat's, for the BIaaS deployment (the owner, 2026-10-27): the Accobat logo
 * - 220 x 55, the "Know your business" line part of the image - linking to
 * accobat.com, Accobat on LinkedIn, the BIaaS strapline, and the links with
 * their labels exactly as given - two of the product names are Danish, as
 * accobat.com has them.
 */
export const BIAAS_FOOTER: CompanyFooterContent = {
  site: 'https://www.accobat.com/',
  logo: { src: accobatLogo, alt: 'Accobat', width: 168, height: 42 },
  strapline: 'Business Intelligence as a Service - and the knowledge to make it work.',
  linkedIn: { href: 'https://www.linkedin.com/company/accobat-as/?viewAsMember=true' },
  columns: [
    {
      title: 'product',
      links: [
        { label: 'Datadrevet Ejendom', href: 'https://www.accobat.com/dde' },
        { label: 'Datadrevet Uddannelse', href: 'https://www.accobat.com/datadrevet_uddannelse' },
        { label: 'BI for Business Central', href: 'https://www.accobat.com/bi-for-business-central' },
        { label: 'BI for D365 FO', href: 'https://www.accobat.com/bi-for-d365' },
      ],
    },
    {
      title: 'resources',
      links: [
        { label: 'News', href: 'https://www.accobat.com/acconews' },
        { label: 'Feedback', href: 'https://biaas.feedback.accobat.com/' },
      ],
    },
    {
      title: 'contact',
      links: [{ label: 'Contact us', href: 'https://www.accobat.com/kontakt-os' }],
    },
  ],
};
